import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trash2, Plus, Maximize2, Info, Play, ImageIcon, ArrowRight } from "lucide-react";
import { ArrowLeft } from "lucide-react";
import moment from "moment";
import { toast } from "react-toastify";
import Tooltip from "@mui/material/Tooltip";
import {
  get_event_media,
  upload_event_media,
  delete_event_media,
} from "../Redux/Action/EventAction";
import UploadMediaModal from "../Modal/UploadMediaModal";
import DeleteMediaConfirmModal from "../Modal/DeleteMediaConfirmModal";
import useTitle from "../Hooks/useTitle";
import notfound from "../assets/images/no-result.png";

const BASE_URL = "https://api.happiclips.com/";

const SkeletonCard = () => (
  <div className="relative bg-white rounded-2xl overflow-hidden shadow-lg animate-pulse">
    <div className="w-full h-[300px] bg-gray-200" />
    <div className="absolute top-3 left-3 bg-gray-300 h-6 w-20 rounded-full" />
    <div className="absolute bottom-0 left-0 right-0 bg-gray-300 h-12" />
  </div>
);

const MediaCard = ({ item, index, setPreviewIndex, setItemToDelete, setShowDeleteConfirm, handleVideoPlay }) => (
  <div className="relative group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
    <div className="relative w-full h-[300px]">
      {item.media_type === "photo" ? (
        <img
          src={`${BASE_URL}${item.media_path}`}
          alt="Media"
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
          onClick={() => setPreviewIndex(index)}
        />
      ) : (
        <div className="relative w-full h-full">
          <video
            src={`${BASE_URL}${item.media_path}`}
            className="w-full h-full object-cover"
            controls
            onClick={(e) => e.stopPropagation()}
            onPlay={() => handleVideoPlay(index)}
          />
        </div>
      )}

      <div className="absolute top-3 left-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
        {item.media_type === "photo" ? <ImageIcon size={14} /> : <Play size={14} />}
        {item.media_type.toUpperCase()}
      </div>

      {/* Delete Button */}
      <div className="absolute top-3 right-12 opacity-0 group-hover:opacity-100 transition-all duration-300 flex gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setItemToDelete(item);
            setShowDeleteConfirm(true);
          }}
          className="bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
        >
          <Trash2 size={16} />
        </button>
      </div>
      {/* Preview Button */}
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 flex gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setPreviewIndex(index);
          }}
          className="bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
        >
          <Maximize2 size={16} />
        </button>
      </div>

      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 text-white">
        <Tooltip title={moment(item.updated_at).format("MMM DD, YYYY hh:mm A")} arrow placement="top">
          <button className="flex items-center gap-1 text-xs hover:text-blue-200 transition-colors">
            <Info size={14} />
            <span>{moment(item.updated_at).fromNow()}</span>
          </button>
        </Tooltip>
      </div>
    </div>
  </div>
);

const MediaGallery = () => {
  useTitle("Media Gallery");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");
  const [isDragOver, setIsDragOver] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [mediaList, setMediaList] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isPreviewVideoReady, setIsPreviewVideoReady] = useState(false);
  const isFetchingRef = useRef(false);
  const previewVideoRef = useRef(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const { eventId } = useLocation().state;
  const [deleteLoader, setDeleteLoader] = useState(false);
  const { t } = useTranslation("media_gallery");
  const { loading, eventMedia, error, mediaItemList, lastPage } = useSelector((state) => state.Create);

  const filteredItems = mediaList.filter(
    (item) => activeFilter === "all" || item.media_type === activeFilter
  );

  useEffect(() => {
    dispatch(get_event_media(eventId, 1));
  }, [dispatch, eventId]);

  useEffect(() => {
    if (mediaItemList) {
      setTotalPages(lastPage);
      setMediaList((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const newItems = mediaItemList.filter((item) => !existingIds.has(item.id));
        return currentPage === 1 ? mediaItemList : [...prev, ...newItems];
      });
      setHasMore(currentPage < lastPage);
    }
  }, [mediaItemList, currentPage, lastPage]);

  useEffect(() => {
    if (eventMedia) {
      closeModal();
      dispatch(get_event_media(eventId, currentPage));
    }
    if (error) {
      toast.error(error);
    }
  }, [eventMedia, error, dispatch, eventId, currentPage]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setPreviewIndex(null);
      if (e.key === "ArrowRight" && previewIndex !== null) handlePreviewNavigation("next");
      if (e.key === "ArrowLeft" && previewIndex !== null) handlePreviewNavigation("prev");
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [previewIndex, mediaList.length, currentPage, hasMore]);

  const handlePreviewNavigation = async (direction) => {
    if (previewLoading || isFetchingRef.current) return;

    const isNext = direction === "next";
    const filteredItems = mediaList.filter((item) => activeFilter === "all" || item.media_type === activeFilter);
    let newIndex = isNext ? previewIndex + 1 : previewIndex - 1;

    if (isNext && newIndex >= filteredItems.length) {
      if (hasMore && !isFetchingRef.current) {
        try {
          isFetchingRef.current = true;
          setPreviewLoading(true);

          const nextPage = currentPage + 1;
          setCurrentPage(nextPage);
          await dispatch(get_event_media(eventId, nextPage));

          const addedFiltered = mediaItemList ? mediaItemList.filter((item) => activeFilter === "all" || item.media_type === activeFilter) : [];
          if (addedFiltered.length > 0) {
            setIsPreviewVideoReady(false);
            setPreviewIndex(filteredItems.length);
          } else {
            if (!hasMore || nextPage >= totalPages) {
              setIsPreviewVideoReady(false);
              setPreviewIndex(0);
            } else {
              setIsPreviewVideoReady(false);
              setPreviewIndex(filteredItems.length - 1);
            }
          }
        } finally {
          setPreviewLoading(false);
          isFetchingRef.current = false;
        }
        return;
      } else {
        newIndex = 0;
      }
    }

    if (!isNext && newIndex < 0) {
      newIndex = filteredItems.length - 1;
    }

    setIsPreviewVideoReady(false);
    setPreviewIndex(newIndex);
  };

  const openModal = () => {
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = "auto";
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileSelect = (files) => {
    const validFiles = Array.from(files).filter(
      (file) => file.type.startsWith("image/") || file.type.startsWith("video/")
    );
    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const handleDelete = (mediaId) => {
    setDeleteLoader(true);
    dispatch(delete_event_media(eventId, mediaId)).then(() => {
      setShowDeleteConfirm(false);
      setMediaList((prev) => prev.filter((item) => item.id !== mediaId));
      toast.success(t("deleteMessage"));
    }).catch((errorMsg) => toast.error(errorMsg)).finally(() => setDeleteLoader(false));
  };

  const handleLoadMore = () => {
    if (hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      dispatch(get_event_media(eventId, nextPage));
    }
  };

  const handleVideoPlay = (index) => {
    if (playingVideo !== null && playingVideo !== index) {
      const videoElements = document.querySelectorAll("video");
      if (videoElements[playingVideo]) videoElements[playingVideo].pause();
    }
    setPlayingVideo(index);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 md:p-8 mt-[76px] sm:ml-[300px]">
      <div className="mx-auto w-full">
        <header className="mb-4">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{t("title_media_gallery")}</h1>
          <p className="text-gray-600 text-lg">{t("manage_image_n_video")}</p>
        </header>

        <div className="flex flex-wrap justify-between mb-4 items-center gap-4 bg-white p-2 rounded-xl shadow-sm">
          <div className="flex flex-wrap gap-4">
            {["all", "photo", "video"].map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-5 py-2 rounded-lg text-sm font-medium capitalize transition-all duration-200 ${activeFilter === filter
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-gray-700 hover:text-gray-900 hover:bg-gray-200"
                  }`}
              >
                {filter === "all" ? t("all") : t(filter) + "s"}
              </button>
            ))}
          </div>
          <button
            onClick={openModal}
            className="flex items-center gap-2 px-4 py-2 bg-[#3686FF] text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <Plus size={20} />
            {t("add_media")}
          </button>
        </div>

        {loading && !mediaList.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
            {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : filteredItems.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5 auto-rows-[minmax(200px,auto)]">
              {filteredItems.map((item, index) => (
                <MediaCard
                  key={item.id}
                  item={item}
                  index={index}
                  setPreviewIndex={setPreviewIndex}
                  setItemToDelete={setItemToDelete}
                  setShowDeleteConfirm={setShowDeleteConfirm}
                  handleVideoPlay={handleVideoPlay}
                />
              ))}
            </div>
            {currentPage < totalPages && (
              <div className="flex justify-center mt-12">
                <button
                  onClick={handleLoadMore}
                  className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : null}
                  {loading ? t("loading") : t("load_more")}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-center h-[60vh] bg-white rounded-2xl shadow-sm">
            <img src={notfound} alt="No Media" className="w-48 h-48 mb-6 opacity-80" />
            <h3 className="text-2xl font-semibold text-gray-700 mb-2">{t("no_media_found")}</h3>
            <p className="text-gray-500 mb-6 max-w-md">
              Get started by uploading your first photo or video to create an amazing media gallery.
            </p>
            <button
              onClick={openModal}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 shadow-md transition-all duration-300"
            >
              <Plus size={20} />
              Upload Your First Media
            </button>
          </div>
        )}

        {isModalOpen && (
          <UploadMediaModal
            isDragOver={isDragOver}
            handleDragOver={(e) => e.preventDefault()}
            handleDragLeave={(e) => e.preventDefault()}
            handleDrop={(e) => handleFileSelect(e.dataTransfer.files)}
            fileInputRef={fileInputRef}
            handleFileSelect={handleFileSelect}
            selectedFiles={selectedFiles}
            formatFileSize={(b) => (b / 1024 / 1024).toFixed(2)}
            removeFile={(i) => setSelectedFiles((prev) => prev.filter((_, idx) => idx !== i))}
            closeModal={closeModal}
            addMedia={() => dispatch(upload_event_media(eventId, selectedFiles))}
            loading={loading}
          />
        )}

        {showDeleteConfirm && itemToDelete && (
          <DeleteMediaConfirmModal
            onCancel={() => setShowDeleteConfirm(false)}
            onDelete={() => handleDelete(itemToDelete.id)}
            deleteLoader={deleteLoader}
          />
        )}

        {previewIndex !== null && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center">
            <button
              onClick={() => setPreviewIndex(null)}
              className="absolute top-6 right-6 text-white text-4xl z-10 bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-70 transition-colors"
            >
              ✕
            </button>
            <div className="relative w-full h-full flex items-center justify-center">
              {filteredItems[previewIndex].media_type === "photo" ? (
                <img
                  src={`${BASE_URL}${filteredItems[previewIndex].media_path}`}
                  alt="Preview"
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <video
                  key={filteredItems[previewIndex].id}
                  ref={previewVideoRef}
                  src={`${BASE_URL}${filteredItems[previewIndex].media_path}`}
                  className="max-w-full max-h-full"
                  controls
                  autoPlay
                  muted
                  playsInline
                  preload="auto"
                  onCanPlay={() => {
                    setIsPreviewVideoReady(true);
                    try { previewVideoRef.current && previewVideoRef.current.play(); } catch (e) { /* ignore */ }
                  }}
                  onLoadedData={() => setIsPreviewVideoReady(true)}
                />
              )}
              {(previewLoading || (filteredItems[previewIndex].media_type === "video" && !isPreviewVideoReady)) && (
                <div className="absolute inset-0 flex items-center justify-center text-white text-lg bg-black/20">Loading...</div>
              )}
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-4 py-2 rounded-full text-sm">
                {previewIndex + 1} of {filteredItems.length}
              </div>
            </div>
            {filteredItems.length > 1 && (
              <>
                <button
                  className="absolute flex items-center justify-center left-6 top-1/2 transform -translate-y-1/2 text-white text-5xl bg-black bg-opacity-50 rounded-full p-3 hover:bg-opacity-70 transition-colors h-10 w-10"
                  onClick={() => handlePreviewNavigation("prev")}
                  disabled={previewLoading}
                >
                  <ArrowLeft size={20} />
                </button>
                <button
                  className="absolute flex items-center justify-center right-6 top-1/2 transform -translate-y-1/2 text-white text-5xl bg-black bg-opacity-50 rounded-full p-3 hover:bg-opacity-70 transition-colors h-10 w-10"
                  onClick={() => handlePreviewNavigation("next")}
                  disabled={previewLoading}
                >
                  <ArrowRight size={20} />
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <style jsx>{`
        .masonry-grid {
          grid-template-rows: masonry;
        }
        @media (min-width: 640px) {
          .masonry-grid {
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          }
        }
      `}</style>
    </div>
  );
};

export default MediaGallery;